import { GetOrganizationDto } from 'woofwoof-api-public';
import i18n from '../locales/i18n';

function timeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

function isCurrentlyOnTimeOff(
  timeOffs: Array<{
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
  }>,
): { isOnTimeOff: boolean; timeOffEnd?: string } {
  const now = new Date();

  for (const off of timeOffs) {
    const offStart = new Date(off.startDate);
    const offEnd = new Date(off.endDate);

    if (off.startTime && off.endTime) {
      if (now >= offStart && now < offEnd) {
        return { isOnTimeOff: true, timeOffEnd: off.endTime };
      }
    } else {
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(now);
      todayEnd.setHours(23, 59, 59, 999);

      const offStartDay = new Date(offStart);
      offStartDay.setHours(0, 0, 0, 0);
      const offEndDay = new Date(offEnd);
      offEndDay.setHours(23, 59, 59, 999);

      if (todayStart >= offStartDay && todayStart <= offEndDay) {
        return { isOnTimeOff: true };
      }
    }
  }

  return { isOnTimeOff: false };
}

export function getOrganizationOpeningStatus(
  workingHours: GetOrganizationDto['workingHours'],
  timeOffs: Array<{
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
  }> = [],
) {
  const now = new Date();
  // Stored convention: 0 = Sunday … 6 = Saturday (AvailabilitySlotDto.weekday).
  const currentWeekday = now.getDay();

  const currentTime = now.getHours() * 60 + now.getMinutes();

  const timeOffStatus = isCurrentlyOnTimeOff(timeOffs);
  if (timeOffStatus.isOnTimeOff) {
    if (timeOffStatus.timeOffEnd) {
      return {
        isOpen: false,
        text: i18n.t('openingStatus.closedOpensAt', {
          time: timeOffStatus.timeOffEnd,
        }),
        shortText: i18n.t('openingStatus.closedToday'),
        shortTextHours: i18n.t('openingStatus.opensAt', {
          time: timeOffStatus.timeOffEnd,
        }),
      };
    }
    return {
      isOpen: false,
      text: i18n.t('openingStatus.closedDayOffFull'),
      shortText: i18n.t('openingStatus.closedToday'),
      shortTextHours: i18n.t('openingStatus.dayOff'),
    };
  }

  const todayHours =
    workingHours?.filter((slot) => slot.weekday === currentWeekday) || [];

  const currentSlot = todayHours.find((slot) => {
    const startTime = timeToMinutes(slot.start);
    const endTime = timeToMinutes(slot.end);

    return currentTime >= startTime && currentTime < endTime;
  });

  if (currentSlot) {
    const localEndTime = currentSlot.end;
    return {
      isOpen: true,
      text: i18n.t('openingStatus.openClosesAtFull', { time: localEndTime }),
      shortText: i18n.t('openingStatus.openToday'),
      shortTextHours: i18n.t('openingStatus.closesAt', { time: localEndTime }),
    };
  }

  const laterTodaySlot = todayHours.find((slot) => {
    const startTime = timeToMinutes(slot.start);
    return currentTime < startTime;
  });

  if (laterTodaySlot) {
    const localStartTime = laterTodaySlot.start;
    return {
      isOpen: false,
      text: i18n.t('openingStatus.closedOpensLaterToday', {
        time: localStartTime,
      }),
      shortText: i18n.t('openingStatus.closedToday'),
      shortTextHours: i18n.t('openingStatus.opensAt', { time: localStartTime }),
    };
  }

  for (let i = 1; i <= 7; i += 1) {
    const nextWeekday = (currentWeekday + i) % 7;
    const nextDayHours =
      workingHours?.filter((slot) => slot.weekday === nextWeekday) || [];

    if (nextDayHours.length > 0) {
      const earliestSlot = nextDayHours.reduce((earliest, slot) => {
        const earliestTime = timeToMinutes(earliest.start);
        const slotTime = timeToMinutes(slot.start);

        return slotTime < earliestTime ? slot : earliest;
      });

      const dayName =
        i === 1
          ? i18n.t('openingStatus.tomorrow')
          : // The weekday labels are keyed 1 = Monday … 7 = Sunday.
            i18n.t(
              `openingStatus.weekdays.${nextWeekday === 0 ? 7 : nextWeekday}`,
            );
      const localStartTime = earliestSlot.start;
      return {
        isOpen: false,
        text: i18n.t('openingStatus.closedOpensOnDayAt', {
          day: dayName,
          time: localStartTime,
        }),
        shortText: i18n.t('openingStatus.closedToday'),
        shortTextHours: i18n.t('openingStatus.opensDayAt', {
          day: dayName,
          time: localStartTime,
        }),
      };
    }
  }

  return {
    isOpen: false,
    text: i18n.t('openingStatus.closedToday'),
    shortText: i18n.t('openingStatus.closedToday'),
    shortTextHours: i18n.t('openingStatus.checkHours'),
  };
}
